export const statisticsModeMinPrivilege = 3;

export const noFlowCalculationFollowsVisibility = true;

export const featureMinPrivileges = { excludeTimes: 4 };

export const dataSource = {
  endpoint: '/api/v1/productionline/flowStatistic/flow_data/',
  groupBy: 'identifier',
  identifiersParam: 'identifiers',
  wsType: 'flow-stats-data',
  staleThresholdMs: 90_000, // LIDAR/REMA send every 60s, so 90s threshold
  // Backend identifiers: what we actually request from the API
  // transformEntry remaps INFEED_EMPTY → INFEED_STATUS, STORAGE_FULL → STORAGE_STATUS
  backendIdentifiers: ['LIDAR_LAUFMETERS', 'REMA_LAUFMETERS', 'INFEED_EMPTY', 'STORAGE_FULL', 'DOOR', 'ERROR', 'AUTO', 'EXIT_FULL']
};

const zincTarget = { minArea: 7500, maxArea: 11000, minAreaSpeed: 2523, maxAreaSpeed: 2013, referenceLength: 3000 };

export const statisticsDatasets = [
  // {
  //   key: 'B10',
  //   label: 'B10',
  //   allowStopAnnotations: true,
  //   defaultExcludedTimes: [{ from: 'productionEnd', to: 'productionStart' }],
  //   efficiencyAreaIdentifiers: ['B10'],
  //   errorsAreaIdentifiers: ['B10'],
  //   doublesAreaIdentifiers: ['B10'],
  //   averageLengthIdentifier: 'XCUT_BOARD_LENGTH',
  //   color: '#22c55e'
  // },
  {
    key: 'B30',
    label: 'Finger jointer',
    cameraNames: ['Cam_10_9_250_34', 'B14 - Cutters Grecon', 'B15 - Incline Chain', 'IP CAMERA', 'Cam3-BeforePlaner Port 15', 'Cam4-AfterPlaner Port 16', 'Cam3-StorageAB-Port 17'],
    actualSpeedLabel: 'Actual avg. capacity',
    expectedSpeedLabel: 'Expected avg. capacity',
    allowStopAnnotations: true,
    defaultExcludedTimes: [{ from: 'productionEnd', to: 'productionStart' }],
    // Cards are enabled and displayed in this order.
    productionCards: ['runningMetres', 'productionTime', 'bestHour'],
    summaryCards: ['actualSpeedAverage', 'expectedSpeedAverage', 'efficiency', 'averageLength'],
    highlightBestHour: true,
    allowGapExclusions: true,
    showXcutMeasurements: true,
    showDimensionSpectrogram: true,
    speedUnit: 'm/h',
    expectedSpeedFormula: zincTarget,
    calculateExpectedSpeed: (area, averageLength) => {
      const { minArea, maxArea, minAreaSpeed, maxAreaSpeed, referenceLength } = zincTarget;
      const clampedArea = Math.max(minArea, Math.min(area, maxArea));
      const metresPerHour = minAreaSpeed - ((clampedArea - minArea) * (minAreaSpeed - maxAreaSpeed)) / (maxArea - minArea);
      return (metresPerHour * Math.min(averageLength / referenceLength, 1)) / 60;
    },
    efficiencyAreaIdentifiers: ['B30'],
    errorsAreaIdentifiers: ['B30'],
    doublesAreaIdentifiers: ['B30'],
    averageLengthIdentifier: 'XCUT_BOARD_LENGTH',
    color: '#84cc16'
  }
  // {
  //   key: 'C10',
  //   label: 'C10',
  //   allowStopAnnotations: true,
  //   defaultExcludedTimes: [{ from: 'productionEnd', to: 'productionStart' }],
  //   efficiencyAreaIdentifiers: ['C10'],
  //   errorsAreaIdentifiers: ['C10'],
  //   doublesAreaIdentifiers: ['C10'],
  //   color: '#f59e0b'
  // }
];

// Reuse saved Fiber footprints. B21/B22 and B33 retain the existing snake's dataset ownership.
export const productionFlowAreas = [
  { areaName: 'B11', datasetKey: 'B10', direction: '-z' },
  { areaName: 'B12', datasetKey: 'B10', direction: '+z' },
  { areaName: 'B13', datasetKey: 'B10', direction: '-x' },
  { areaName: 'B14', datasetKey: 'B10', direction: '-x' },
  { areaName: 'B15', datasetKey: 'B10', direction: '-z' },
  { areaName: 'B21', datasetKey: 'B10', direction: '-x' },
  { areaName: 'B22', datasetKey: 'B10', direction: '-x' },
  { areaName: 'B31', datasetKey: 'B30', direction: '+z' },
  { areaName: 'B32', datasetKey: 'B30', direction: '+z' },
  { areaName: 'B33', datasetKey: 'B30', direction: '-x' },
  { areaName: 'C11', datasetKey: 'C10', direction: '-x' },
  { areaName: 'C12', datasetKey: 'C10', direction: '+x' },
  { areaName: 'C13', datasetKey: 'C10', direction: '+x' }
];

export const productionFlowLines = [
  {
    key: 'main-production-flow',
    points: [
      { id: 'B13', datasetKey: 'B10', position: [-12, 4, -8] },
      { id: 'B15', position: [-26, 4, -8] },
      { id: 'B21', position: [-29, 4, -19] },
      { id: 'B22', position: [-42, 4, -18] },
      { id: 'B31', datasetKey: 'B30', position: [-43, 4, 0] },
      { id: 'B33', datasetKey: 'C10', position: [-80, 4, 2] },
      { id: 'C10', position: [-120, 4, 3] },
      { id: 'C11', position: [-120, 4, 16] },
      { id: 'C12', position: [-82, 4, 19] }
    ]
  }
];

// Remap status identifiers to virtual tab identifiers
const STATUS_MAP = {
  INFEED_EMPTY: 'INFEED_STATUS',
  STORAGE_FULL: 'STORAGE_STATUS',
  DOOR: 'DOOR_STATUS',
  ERROR: 'ERROR_STATUS',
  AUTO: 'AUTO_STATUS',
  EXIT_FULL: 'EXIT_STATUS'
};

export const transformEntry = (entry) => {
  const rawIdentifier = entry.identifier ?? '';
  const mappedIdentifier = STATUS_MAP[rawIdentifier] ?? rawIdentifier;
  const dataFields = entry.data ?? {};

  const point = {
    date: (entry.timestamp ?? '').slice(0, 19),
    identifier: mappedIdentifier
  };

  // Laufmeters/pieces — present on LIDAR/REMA rows
  const lauf = dataFields.laufmeters;
  point.laufmeters = typeof lauf === 'number' ? parseFloat(lauf.toFixed(4)) : 0;
  const pcs = dataFields.pieces;
  point.pieces = typeof pcs === 'number' ? parseFloat(pcs.toFixed(4)) : 0;

  // Status — aggregated data stores minutes directly; raw data falls back to 0 or 1
  const statusField = (field, rawId) => (typeof dataFields[field] === 'number' ? parseFloat(dataFields[field].toFixed(2)) : rawIdentifier === rawId ? 1 : 0);

  point.infeedEmpty = statusField('infeedEmpty', 'INFEED_EMPTY');
  point.storageFull = statusField('storageFull', 'STORAGE_FULL');
  point.door = statusField('door', 'DOOR');
  point.error = statusField('error', 'ERROR');
  point.auto = statusField('auto', 'AUTO');
  point.exitFull = statusField('exitFull', 'EXIT_FULL');

  return point;
};

const config = {
  statisticsModeMinPrivilege,
  featureMinPrivileges,
  defaultDatasetKey: 'B30',
  signalOverlapLast: true,
  dataSource,
  statisticsDatasets,
  productionFlowAreas: typeof productionFlowAreas === 'undefined' ? undefined : productionFlowAreas,
  productionFlowLines: typeof productionFlowLines === 'undefined' ? [] : productionFlowLines,
  transformEntry,
  showOverview: false,
  showContractEfficiency: false,
  showExpectedContractSpeed: false
};
export default config;
